import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import i18n from 'i18n-js';

import { Autocomplete } from 'components/FormFields';
import { PricingActions } from 'state/modules/pricing';
import { usePrevious } from 'hooks';
import { truncateKeywordSpaces } from 'utils/utils';

const InvoiceTypeSelect = ({
  label,
  variant,
  field: { name, value },
  form: { errors, touched, setFieldValue },
  customParams,
  disabled,
  margin,
  onChange,
}) => {
  const dispatch = useDispatch();
  const firstRender = useRef(true);
  const [defaultValue, setDefaultValue] = useState(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    status: 'ACTIVE',
    ...customParams,
  });

  const { adhocs, fetchingAdhocs } = useSelector((state) => state.pricing);

  const previd = usePrevious(value);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchAdHocsTypes = useCallback(
    debounce((params, list) => {
      const newParams = { ...params };
      setParams(newParams);
      if (newParams?.keyword) {
        newParams.keyword = truncateKeywordSpaces(newParams.keyword);
      }
      if (list) {
        return dispatch(PricingActions.fetchAdHocTypesById(value, true));
      }
      dispatch(PricingActions.fetchAdHocTypes(newParams, list));
    }, 1000),
    []
  );

  useEffect(() => {
    if (value) {
      fetchAdHocsTypes({ ...params, ...customParams }, true);
    } else {
      dispatch(PricingActions.resetAdhocTypes());
    }
  }, []);

  useEffect(() => {
    if (previd !== value && (value || value === '' || !defaultValue)) {
      const defaultVal = adhocs.find((el) => el.id === parseInt(value));
      if (defaultVal) {
        if (onChange) {
          onChange(defaultVal);
        }
        setDefaultValue(defaultVal);
      } else {
        fetchAdHocsTypes({ ...params, ...customParams });
        setDefaultValue(null);
      }
    }
  }, [value]); // eslint-disable-line

  useEffect(() => {
    if (adhocs && value && !defaultValue) {
      const defaultVal = adhocs.find((el) => el.id === value);
      if (defaultVal) {
        if (onChange) {
          onChange(defaultVal);
        }
        setDefaultValue(defaultVal);
      } else if (value === '') {
        setDefaultValue('');
      }
    }
  }, [adhocs]); // eslint-disable-line

  const handleFetchAdHocsTypes = () => {
    if (adhocs.length === 0 && !fetchingAdhocs && !params?.keyword)
      dispatch(PricingActions.fetchAdHocTypes(params));
  };

  return (
    <Autocomplete
      label={label}
      margin={margin}
      fullwidth="true"
      options={adhocs}
      variant={variant}
      clearOnBlur={false}
      disabled={disabled}
      onHighlightChange={handleFetchAdHocsTypes}
      value={defaultValue}
      id="invoice-type-select"
      loading={fetchingAdhocs}
      getOptionLabel={(option) =>
        option.name
          ? `${option.name} - ${option.is_revenue ? i18n.t('skus.category_fee') : option.is_hold ? i18n.t('skus.category_hold') : i18n.t('skus.category_credit')}`
          : ''
      }
      onChange={(ev, val) => {
        if (onChange) {
          onChange(val);
        }
        setFieldValue(name, val?.id || '');
      }}
      error={errors && errors[name] && touched && touched[name]}
      getOptionSelected={(option, value) => option.id === value.id}
      onInputChange={(ev, val) => {
        if (!firstRender.current) {
          const split = val?.split(' - ');
          fetchAdHocsTypes({ ...params, keyword: split?.[0] || val });
        } else {
          firstRender.current = false;
        }
      }}
      helperText={errors && errors[name] && touched && touched[name] && errors[name]}
    />
  );
};

InvoiceTypeSelect.propTypes = {
  label: PropTypes.string,
  margin: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
    setFieldValue: PropTypes.func,
  }),
  onChange: PropTypes.func,
  customParams: PropTypes.object,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

InvoiceTypeSelect.defaultProps = {
  required: true,
  disabled: false,
  margin: 'normal',
  form: {
    errors: {},
    touched: {},
  },
  customParams: {},
  variant: 'standard',
  label: 'Select...',
};

export default InvoiceTypeSelect;
